import { TFunction } from 'i18next';
import { MAX_CUIL, MIN_CUIL } from '../../../constants/values';

export type ValidationName =
    | 'required'
    | 'minLength'
    | 'maxLength'
    | 'fixedLength'
    | 'lettersOnly'
    | 'cuil'
    | 'maxValue'
    | 'minValue'
    | 'phoneNumber';

export type ValidationRule = {
    name: ValidationName;
    param?: number;
    errorMessage?: string;
};

export const validationRules = (
    t: TFunction
): Record<ValidationName, (value: any, param?: number, errorMessage?: string) => string> => ({
    required: (value: any) => {
        return value === null || value === undefined || value === ''
            ? t('validation.required')
            : '';
    },
    minLength: (value: any, param?: number) => {
        return param && value && value.length < param ? t('validation.minLength', { param }) : '';
    },
    maxLength: (value: any, param?: number) => {
        return param && value && value.length > param ? t('validation.maxLength', { param }) : '';
    },
    lettersOnly: (value: any) => {
        const regex = /^[a-zA-Z\s]*$/;
        return !regex.test(value) ? t('validation.lettersOnly') : '';
    },
    fixedLength: (value: any, param?: number) => {
        return param && value && value.length !== param
            ? t('validation.fixedLength', { param })
            : '';
    },
    maxValue: (value: any, param?: number) => {
        const numberValue = Number(value);
        if (isNaN(numberValue)) return t('validation.invalidNumber');
        return param !== undefined && numberValue > param
            ? t('validation.maxValue', { param })
            : '';
    },
    minValue: (value: any, param?: number) => {
        const numberValue = Number(value);
        if (isNaN(numberValue)) return t('validation.invalidNumber');
        return param !== undefined && numberValue < param
            ? t('validation.minValue', { param })
            : '';
    },
    cuil: (value: any) => {
        const cuilNumber = Number(value);

        if (cuilNumber < MIN_CUIL || cuilNumber > MAX_CUIL) {
            return t('validation.invalidCUIL');
        }
        return '';
    },
    phoneNumber: (value: any) => {
        if (value === null || value === undefined || value === '') return '';

        if (isNaN(+value)) return t('validation.invalidPhoneNumber');

        const length = value.toString().length;
        if (length < 10) return t('validation.phoneNumberTooShort');
        if (length > 15) return t('validation.phoneNumberTooLong');

        return '';
    }
});

const useValidation = (t: TFunction) => {
    const validateRules = validationRules(t);

    const validate = (value: any, validations: ValidationRule[]) => {
        let hasError = false;
        let errorText = '';

        for (const validation of validations) {
            const validationFn = validateRules[validation.name];
            if (validationFn) {
                const error = validationFn(value, validation.param);
                if (error) {
                    hasError = true;
                    errorText = error;
                    break;
                }
            }
        }

        return { hasError, errorText };
    };

    return { validate };
};

export default useValidation;
